
import { js_logs } from '../settings';

export const mwl_log = (...args) => {
    if (js_logs) {
        console.log(...args);
    }
};

export const transformAnimationType = (animationType) => {
    switch (animationType) {
        case 'zoom_in_out':
            return 'zoom';
        case 'fade':
            return 'fade';
        case 'none':
            return 'none';
        default:
            return 'zoom';
    }
};

export const preventRightClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    return false;
};

export const initializeMapInContainer = (containerId, imageData) => {
    if (typeof window.mwlInitMap === 'undefined') {
        console.error('window.mwlInitMap is not defined. Please check that you have included the map script in your page.');
        return;
    }

    const gps = imageData.exif.gps;

    if (!gps || gps === 'N/A') {
        console.warn('No GPS data available for this image.');
        return;
    }

    const gpsCoords = gps.split(',');
    const lat = parseFloat(gpsCoords[0]);
    const lng = parseFloat(gpsCoords[1]);

    const image = {
        gps: {
            lat: lat,
            lng: lng,
        },
        src: imageData.src,
        dimensions: {
            width: imageData.width,
            height: imageData.height,
        },
        low_res_src: imageData.msrc,
        exifs: {
            caption: imageData.meta.caption,
        },
        title: imageData.meta.title,
    };

    window.mwlInitMap(containerId, image);
};

// Helper function to create an orphan data entry
export const createOrphanEntry = (id, imgElement) => {
  const src    = imgElement.getAttribute('mgl-src') || imgElement.src || '';
  const srcset = imgElement.getAttribute('mgl-srcset') || imgElement.srcset || '';
  return {
    id: id,
    src: src,
    srcset: srcset,
    msrc: src,
    width: imgElement.naturalWidth || 800,
    height: imgElement.naturalHeight || 600,
    thumbCropped: true,
    meta: {
      title: imgElement.alt || '',
      caption: '',
      description: '',
      keywords: '',
      author: '',
      date: '',
      download: src,
    },
    exif: {
      camera: '',
      focal_length: '',
      shutter_speed: '',
      aperture: '',
      iso: '',
      lens: '',
      date: '',
      keywords: '',
      gps: null,
      copyright: '',
    },
  };
};

// Helper function to create an orphan mwl_data entry (for window.mwl_data)
export const createOrphanMwlDataEntry = (id, imgElement) => {
  const src    = imgElement.getAttribute('mgl-src') || imgElement.src || '';
  const srcset = imgElement.getAttribute('mgl-srcset') || imgElement.srcset || '';

  return {
    data: {
      id: id,
      title: imgElement.alt || '',
      caption: '',
      description: '',
      keywords: '',
      author: '',
      date: '',
      camera: '',
      focal_length: '',
      shutter_speed: '',
      aperture: '',
      iso: '',
      lens: '',
      gps: null,
      copyright: '',
    },
    file: src,
    srcset: srcset,
    dimension: {
      width: imgElement.getAttribute('width') || imgElement.naturalWidth || 800,
      height: imgElement.getAttribute('height') || imgElement.naturalHeight || 600,
    },
    download_link: src,
  };
};

// Helper function to find orphan images (images in selector without mwl_data)
export const findOrphanImages = (selectorStr, antiSelectorStr, existingMwlData) => {
  let effectiveSelector = selectorStr;
  if (!effectiveSelector || effectiveSelector.trim() === '') {
    effectiveSelector = '.entry-content, .gallery, .mgl-gallery, .wp-block-gallery, .wp-block-image';
  }
  
  // Get existing IDs from mwl_data
  const existingIds = existingMwlData ? Object.keys(existingMwlData) : [];
  
  // Build a map of URL -> ID from existing mwl_data to detect duplicates
  const urlToIdMap = new Map();
  if (existingMwlData) {
    Object.entries(existingMwlData).forEach(([id, entry]) => {
      if (entry?.file) {
        urlToIdMap.set(entry.file, id);
      }
    });
  }
  
  // Parse anti-selectors into an array
  const antiSelectors = antiSelectorStr ? antiSelectorStr.split(',').map(s => s.trim()).filter(Boolean) : [];
  
  const orphans = [];
  let orphanIdCounter = 0;
  
  const containers = document.querySelectorAll(effectiveSelector);
  
  containers.forEach(container => {
    // Skip if container matches any anti-selector
    if (antiSelectors.length > 0) {
      const matchesAnti = antiSelectors.some(anti => {
        try {
          return container.matches(anti);
        } catch (e) {
          return false;
        }
      });
      if (matchesAnti) return;
    }
    
    const images = container.querySelectorAll('img');
    
    images.forEach(img => {
      // Skip if image matches any anti-selector or is inside an anti-selector element
      if (antiSelectors.length > 0) {
        const matchesAnti = antiSelectors.some(anti => {
          try {
            return img.matches(anti) || img.closest(anti);
          } catch (e) {
            return false;
          }
        });
        if (matchesAnti) return;
      }
      
      // Check if image has mwl-img-id AND if that ID exists in mwl_data
      const existingMwlId = img.getAttribute('data-mwl-img-id');
      if (existingMwlId && existingIds.includes(existingMwlId)) {
        // Image already has valid mwl_data, skip it
        return;
      }
      
      // Check if image has wp-image-{id} class - these are WordPress media library images
      // They should be processed by the normal flow, not as orphans
      const classAttr = img.getAttribute('class') || '';
      const wpImageMatch = classAttr.match(/wp-image-(\d+)/);
      if (wpImageMatch) {
        // This is a WordPress media library image - skip it, let normal processing handle it
        return;
      }
      
      // Skip images without src
      if (!img.src) {
        return;
      }
      
      // Skip tiny images (likely icons, avatars, etc.)
      if (img.naturalWidth > 0 && img.naturalWidth < 50) {
        return;
      }
      
      // Check if this image URL already exists in mwl_data
      // If so, reuse that ID instead of creating a duplicate orphan entry
      const imgSrc = img.getAttribute('mgl-src') || img.src || '';
      const existingIdForUrl = urlToIdMap.get(imgSrc);
      if (existingIdForUrl) {
        // Image URL already exists in mwl_data, reuse the existing ID
        img.setAttribute('data-mwl-img-id', existingIdForUrl);
        return;
      }
      
      // Generate a unique orphan ID
      const orphanId = `orphan-${orphanIdCounter++}`;
      img.setAttribute('data-mwl-img-id', orphanId);
      
      // Add this orphan's URL to the map to prevent duplicates within orphans
      urlToIdMap.set(imgSrc, orphanId);
      
      // Create orphan entry using helper
      orphans.push(createOrphanEntry(orphanId, img));
    });
  });
  
  return orphans;
};